import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, Cloud, Bell, Download, LogOut, ChevronRight, Users } from 'lucide-react';
import { useTheme, THEMES } from '../theme/ThemeProvider';
import { useAuth } from '../auth/AuthProvider';
import { Eyebrow } from '../components/ui';
import { useToast } from '../components/Toast';
import { isSubscribed, pushSupported, subscribePush, unsubscribePush } from '../lib/push';

// Swatch per ogni tema: coppia bg/accent (DESIGN_TOKENS).
const SWATCH: Record<string, { bg: string; accent: string }> = {
  vault: { bg: '#0B0B0D', accent: '#E8B24A' },
  aurora: { bg: '#0A0A12', accent: '#8B7CFF' },
  carbon: { bg: '#0C0D0C', accent: '#C9F24E' },
  sereno: { bg: '#F6F8FB', accent: '#2563EB' },
  terra: { bg: '#F4EFE7', accent: '#C2603A' },
  mezzanotte: { bg: '#080C16', accent: '#39D5E0' },
};

export function ProfiloScreen() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, status, logout } = useAuth();
  const { show } = useToast();
  const [installEvent, setInstallEvent] = useState<Event | null>(null);
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    void isSubscribed().then(setPushOn);
  }, []);

  const togglePush = async () => {
    if (!pushSupported()) return show('Notifiche non supportate da questo browser');
    try {
      if (pushOn) {
        await unsubscribePush();
        setPushOn(false);
        show('Avvisi disattivati');
      } else {
        const ok = await subscribePush();
        setPushOn(ok);
        show(ok ? 'Avvisi attivati' : 'Permesso negato');
      }
    } catch (e) {
      show(e instanceof Error ? e.message : 'Operazione non riuscita');
    }
  };

  // Cattura beforeinstallprompt per la voce "Installa app (PWA)".
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const onInstall = async () => {
    if (installEvent && 'prompt' in installEvent) {
      await (installEvent as unknown as { prompt: () => Promise<void> }).prompt();
      setInstallEvent(null);
    } else {
      show('App già installata o non disponibile');
    }
  };

  const initial = (user?.display_name ?? '?').charAt(0).toUpperCase();

  return (
    <div className="screen">
      {/* Header tappabile → Modifica profilo (§6.11) */}
      <button
        type="button"
        onClick={() => navigate('/profilo/modifica')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 24,
          width: '100%',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          padding: 0,
          color: 'var(--text)',
        }}
      >
        {user?.avatar_url ? (
          <img className="avatar avatar--lg" src={user.avatar_url} alt="" style={{ objectFit: 'cover' }} />
        ) : (
          <div className="avatar avatar--lg">{initial}</div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{user?.display_name ?? '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{user?.email ?? ''}</div>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--faint)' }} />
      </button>

      {/* Tema */}
      <Eyebrow>Aspetto · Tema</Eyebrow>
      <div className="themes" style={{ marginTop: 12, marginBottom: 24 }}>
        {THEMES.map((t) => (
          <button
            key={t.id}
            className={`themerow${theme === t.id ? ' is-active' : ''}`}
            onClick={() => setTheme(t.id)}
          >
            <span
              className="themerow__swatch"
              style={{ background: `linear-gradient(135deg, ${SWATCH[t.id].bg} 50%, ${SWATCH[t.id].accent} 50%)` }}
            />
            <div style={{ flex: 1 }}>
              <div className="themerow__name">{t.name}</div>
              <div className="themerow__desc">{t.desc}</div>
            </div>
            {theme === t.id && <Check size={20} style={{ color: 'var(--accent)' }} />}
          </button>
        ))}
      </div>

      {/* Impostazioni */}
      <Eyebrow>Impostazioni</Eyebrow>
      <div className="settings" style={{ marginTop: 8 }}>
        <Setting
          icon={<Lock size={19} />}
          label="Sicurezza · PIN / Face ID"
          trailing={
            user?.security && (user.security.pin_set || user.security.biometric_enabled) ? (
              <span className="badge badge--accent">Attiva</span>
            ) : (
              <ChevronRight size={16} style={{ color: 'var(--faint)' }} />
            )
          }
          onClick={() => navigate('/profilo/sicurezza')}
        />
        <Setting
          icon={<Users size={19} />}
          label="Familiari"
          trailing={<ChevronRight size={16} style={{ color: 'var(--faint)' }} />}
          onClick={() => navigate('/profilo/familiari')}
        />
        <Setting
          icon={<Cloud size={19} />}
          label="Backup Cloudflare"
          trailing={<span className="badge badge--accent">Attivo</span>}
        />
        <Setting
          icon={<Bell size={19} />}
          label="Avvisi scadenze"
          trailing={<span className={`badge${pushOn ? ' badge--accent' : ''}`}>{pushOn ? 'Attivi' : 'Off'}</span>}
          onClick={togglePush}
        />
        <Setting
          icon={<Download size={19} />}
          label="Export & backup"
          trailing={<ChevronRight size={16} style={{ color: 'var(--faint)' }} />}
          onClick={() => navigate('/profilo/export')}
        />
        <Setting icon={<Download size={19} />} label="Installa app (PWA)" onClick={onInstall} />
        {status === 'auth' && (
          <Setting icon={<LogOut size={19} />} label="Esci" onClick={() => void logout()} />
        )}
      </div>

      {status === 'demo' && (
        <p style={{ marginTop: 20, color: 'var(--muted)', fontSize: 12.5 }}>
          Modalità dimostrativa: dati di esempio locali. Avvia il backend (wrangler) per usare il tuo archivio.
        </p>
      )}
    </div>
  );
}

function Setting({
  icon,
  label,
  trailing,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="setting" onClick={onClick}>
      <span style={{ color: 'var(--accent)' }}>{icon}</span>
      <span className="setting__label">{label}</span>
      {trailing}
    </button>
  );
}
